// src/components/Settings.js
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaSave, FaEye, FaEyeSlash, FaTelegramPlane, FaQuoteLeft, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axios from 'axios';
import QuoteManager from './QuoteManager';

const API_BASE_URL = 'https://gritwears.store/api';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('telegram');
  
  // Telegram Config State
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [showTelegramToken, setShowTelegramToken] = useState(false);
  const [showChatId, setShowChatId] = useState(false);
  const [isTelegramConfigured, setIsTelegramConfigured] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      loadTelegramConfig();
    }
  }, [user]);

  // ============================================
  // TELEGRAM CONFIG FUNCTIONS
  // ============================================
  
  const loadTelegramConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/telegram/${user.uid}`);
      const config = response.data;
      if (config.botToken) {
        setBotToken(config.botToken);
        setChatId(config.chatId);
        setIsTelegramConfigured(true);
      }
    } catch (error) {
      console.error('Error loading Telegram config:', error);
    }
  };

  const saveTelegramConfig = async () => {
    if (!botToken || !chatId) {
      toast.error('Please fill in both fields');
      return;
    }
    
    setTelegramLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/telegram`, {
        userId: user.uid,
        botToken: botToken.trim(),
        chatId: chatId.trim()
      });
      
      if (response.data.success) {
        toast.success('Telegram configuration saved successfully');
        setIsTelegramConfigured(true);
        setTestResult(null);
      } else {
        toast.error(response.data.message || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Error saving configuration');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!botToken || !chatId) {
      toast.error('Please save your configuration first');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/trading-bot/telegram/test`, {
        botToken: botToken.trim(),
        chatId: chatId.trim()
      });
      
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Telegram test message sent!');
      } else {
        toast.error(response.data.message || 'Test failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  const maskString = (str) => {
    if (!str) return '';
    if (str.length <= 8) return '*'.repeat(str.length);
    return str.substring(0, 4) + '*'.repeat(str.length - 8) + str.substring(str.length - 4);
  };

  const tabs = [
    { id: 'telegram', label: 'Telegram Bot', icon: <FaTelegramPlane /> },
    { id: 'quotes', label: 'Motivational Quotes', icon: <FaQuoteLeft /> }
  ];

  return (
    <div className="settings-container">
      {/* Tabs */}
      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`settings-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <div className="settings-tab-content">
        {/* Telegram Bot Tab */}
        {activeTab === 'telegram' && (
          <div className="card">
            <div className="card-header">
              <h2><FaTelegramPlane /> Telegram Bot Configuration</h2>
              {isTelegramConfigured && <span className="configured-badge">✓ Configured</span>}
            </div>
            
            <div className="settings-info">
              <p className="info-text">
                Configure your Telegram bot to receive real-time trading alerts.
                Get your bot token from <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer">@BotFather</a> 
                and your chat ID from <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer">@userinfobot</a>.
              </p>
              <p className="info-note">
                💡 <strong>What you'll receive:</strong> CHoCH, MSS, and CISD pattern alerts, position updates, account balance changes, and trade notifications.
              </p>
            </div>

            <div className="form-group">
              <label>Bot Token:</label>
              <div className="password-input-wrapper">
                <input
                  type={showTelegramToken ? 'text' : 'password'}
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder="Enter your Telegram bot token"
                  className="token-input"
                />
                <button
                  type="button"
                  className="toggle-visibility-btn"
                  onClick={() => setShowTelegramToken(!showTelegramToken)}
                >
                  {showTelegramToken ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {botToken && !showTelegramToken && (
                <div className="masked-preview">Token: {maskString(botToken)}</div>
              )}
            </div>

            <div className="form-group">
              <label>Chat ID:</label>
              <div className="password-input-wrapper">
                <input
                  type={showChatId ? 'text' : 'password'}
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  placeholder="Enter your Telegram chat ID"
                  className="token-input"
                />
                <button
                  type="button"
                  className="toggle-visibility-btn"
                  onClick={() => setShowChatId(!showChatId)}
                >
                  {showChatId ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {chatId && !showChatId && (
                <div className="masked-preview">Chat ID: {maskString(chatId)}</div>
              )}
            </div>

            <div className="settings-actions">
              <button onClick={saveTelegramConfig} className="btn btn-primary" disabled={telegramLoading}>
                <FaSave /> {telegramLoading ? 'Saving...' : 'Save Configuration'}
              </button>
              
              <button 
                onClick={handleTestTelegram} 
                className="btn btn-secondary"
                disabled={testing || !botToken || !chatId}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>

            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                <div className="test-result-icon">
                  {testResult.success ? <FaCheckCircle /> : <FaTimesCircle />}
                </div>
                <div className="test-result-message">
                  <strong>{testResult.success ? 'Test Successful!' : 'Test Failed'}</strong>
                  <p>{testResult.message}</p>
                </div>
              </div>
            )}

            <div className="settings-footer">
              <p className="info-note">📱 <strong>Telegram Commands:</strong></p>
              <ul className="steps-list">
                <li><code>/watch</code> - List all active monitors</li>
                <li><code>/status</code> - Check bot system status</li>
                <li><code>/help</code> - Show help message</li>
              </ul>
            </div>
          </div>
        )}

        {/* Motivational Quotes Tab */}
        {activeTab === 'quotes' && <QuoteManager />}
      </div>
    </div>
  );
};

export default Settings;